import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import API_BASE from '../apiBase';
import { toCommonPRDetails } from '../adapters/prAdapters';

export default function usePRDetails({ provider, identifiers, getProjectKey, fileCache }) {
  const { organization, project, repositoryID, prNumber } = identifiers;
  const [prDetails, setPrDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fileTree, setFileTree] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedFilePath, setSelectedFilePath] = useState('');

  const lastFetchKeyRef = useRef('');

  const normalizePath = useCallback((p) => (p || '').toString().replace(/\\/g, '/').replace(/\s+/g, ' ').trim(), []);

  const buildFileTree = useCallback((files) => {
    const root = { name: '', path: '', children: {} };
    (files || []).forEach((f) => {
      const full = normalizePath(f.path || f.filename || '');
      const parts = full.split('/').filter(Boolean);
      let node = root;
      parts.forEach((part, idx) => {
        const isLeaf = idx === parts.length - 1;
        const childPath = node.path ? `${node.path}/${part}` : part;
        if (!node.children[part]) node.children[part] = { name: part, path: childPath, children: {}, file: null };
        node = node.children[part];
        if (isLeaf) node.file = f;
      });
    });
    return root;
  }, [normalizePath]);

  const getFirstFilePathFromTree = useCallback((root) => {
    if (!root || !root.children) return '';
    const isDir = (node) => Object.keys(node.children || {}).length > 0 && !node.file;
    const entries = Object.values(root.children || {}).sort((a, b) => {
      const aIsDir = isDir(a);
      const bIsDir = isDir(b);
      if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
    for (const child of entries) {
      if (isDir(child)) {
        const found = getFirstFilePathFromTree(child);
        if (found) return found;
      } else if (child && child.file) {
        return normalizePath(child.path);
      }
    }
    return '';
  }, [normalizePath]);

  const makeBasePath = () => {
    if (provider === 'github') return `${API_BASE}/api/github/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}`;
    if (provider === 'bitbucket') return `${API_BASE}/api/bitbucket/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}`;
    if (provider === 'azure') return `${API_BASE}/api/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${prNumber}`;
    if (provider === 'gitlab') {
      const projectID = organization; // for GitLab page we pass projectID in organization slot
      return `${API_BASE}/api/gitlab/projects/${encodeURIComponent(projectID)}/merge-requests/${prNumber}`;
    }
    return `${API_BASE}/api/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${encodeURIComponent(repositoryID)}/${prNumber}`;
  };

  const fetchPRDetails = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const projectKey = getProjectKey();
      if (!projectKey) { setError('Missing project key. Open this PR from the PR list after selecting a project.'); setLoading(false); return; }
      const base = makeBasePath();
      const { data } = await axios.get(`${base}/details`, { headers: { 'X-API-KEY': projectKey } });
      const normalized = toCommonPRDetails(provider, data || {});
      const files = Array.isArray(normalized.files) ? normalized.files : [];
      if (fileCache && typeof fileCache === 'object') {
        for (let i = 0; i < files.length; i++) {
          const f = files[i] || {};
          const key = normalizePath(f.path || f.filename || '');
          const cached = fileCache[key];
          if (cached && typeof cached === 'object') {
            const hasDiff = Array.isArray(f.diff_lines) && f.diff_lines.length > 0;
            const hasComments = Array.isArray(f.comments) && f.comments.length > 0;
            const cachedDiff = Array.isArray(cached.diff_lines) ? cached.diff_lines : [];
            const cachedComments = Array.isArray(cached.comments) ? cached.comments : [];
            if (!hasDiff && cachedDiff.length > 0) f.diff_lines = cachedDiff;
            if (!hasComments && cachedComments.length > 0) f.comments = cachedComments;
          }
        }
      }
      const tree = buildFileTree(files);
      setFileTree(tree);
      setPrDetails(normalized);
      if (!selectedFilePath) {
        const firstPath = getFirstFilePathFromTree(tree);
        setSelectedFilePath(firstPath);
        const initialExpanded = {};
        firstPath.split('/').reduce((acc, seg, idx, arr) => { if (idx < arr.length - 1) { acc.path = acc.path ? `${acc.path}/${seg}` : seg; initialExpanded[acc.path] = true; } return acc; }, { path: '' });
        setExpandedNodes(initialExpanded);
      }
    } catch (error) {
      if (error.response?.status === 401) setError('Authentication failed. Please log in again.');
      else if (error.response?.status === 404) setError('PR details not found. Please check the repository and PR number.');
      else setError(`Failed to fetch PR details: ${error.response?.data?.error || error.message}`);
    }
    setLoading(false);
  }, [provider, organization, project, repositoryID, prNumber, getProjectKey, selectedFilePath, buildFileTree, getFirstFilePathFromTree, fileCache]);

  useEffect(() => {
    const key = `${organization}/${project}/${repositoryID || ''}#${prNumber}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchPRDetails();
  }, [organization, project, repositoryID, prNumber, fetchPRDetails]);

  return {
    prDetails,
    setPrDetails,
    loading,
    error,
    setError,
    fileTree,
    expandedNodes,
    setExpandedNodes,
    selectedFilePath,
    setSelectedFilePath,
    fetchPRDetails,
    normalizePath,
  };
}


